import { useRouter } from '@tanstack/react-router';
import { useCallback } from 'react';

import type { AppLinkOptions } from './links/linkOptions';
import { useNavigate } from './useNavigate';

type BackNavigationOptions = {
    /** Where to go when there is nowhere in the app to go back to. Build it with `linkOptions()`. */
    fallback: AppLinkOptions;
};

interface UseBackNavigationResult {
    navigateBack: (options: BackNavigationOptions) => void;
}

/** Back with a fallback; TanStack's `canGoBack()` is `__TSR_index !== 0`, so entries outside the app don't count. */
export function useBackNavigation(): UseBackNavigationResult {
    const navigate = useNavigate();
    const history = useRouter().history;

    // Landing in a used tab, or back from a full-page auth round trip, is index 0: Back goes to the fallback.
    const navigateBack = useCallback(
        (options: BackNavigationOptions) => {
            if (history.canGoBack()) {
                history.back();
            } else {
                navigate(options.fallback);
            }
        },
        [navigate, history],
    );

    return { navigateBack };
}
